<script setup lang="ts">
import { computed } from 'vue'

interface Props {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger'
  size?: 'sm' | 'md' | 'lg'
  loading?: boolean
  disabled?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'primary',
  size: 'md',
  loading: false,
  disabled: false,
})

const variantClasses: Record<string, string> = {
  primary:
    'bg-primary-500 text-white hover:bg-primary-600 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
  secondary:
    'bg-white text-primary-500 border border-border hover:bg-surface-alt focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
  ghost:
    'bg-transparent text-primary-500 hover:bg-primary-50 focus:ring-2 focus:ring-primary-500',
  danger:
    'bg-danger text-white hover:bg-red-700 focus:ring-2 focus:ring-danger focus:ring-offset-2',
}

const sizeClasses: Record<string, string> = {
  sm: 'px-3 py-1.5 text-sm',
  md: 'px-4 py-2 text-sm',
  lg: 'px-6 py-3 text-base',
}

const computedClasses = computed(() => [
  'inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-colors duration-200',
  variantClasses[props.variant],
  sizeClasses[props.size],
  (props.disabled || props.loading) && 'opacity-50 cursor-not-allowed',
])

const isDisabled = computed(() => props.disabled || props.loading)
</script>

<template>
  <button
    :class="computedClasses"
    :disabled="isDisabled"
    type="button"
  >
    <!-- Loading spinner -->
    <svg
      v-if="loading"
      class="animate-spin -ml-0.5"
      :class="{
        'w-4 h-4': size === 'sm' || size === 'md',
        'w-5 h-5': size === 'lg',
      }"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        class="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        stroke-width="4"
      />
      <path
        class="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
    <slot />
  </button>
</template>
